import { useCallback, useEffect, useRef, useState } from "react";
import type { CSSProperties, KeyboardEvent } from "react";
import type { Link } from "@bronte/viewer/src/data/ViewerConfig";
import { RendererTypes } from "@bronte/viewer/src/main";
import {
  Root,
  Container,
  StyledIframe,
  IframeLabelSection,
  FigureBlock,
  FigCaption,
  CaptionText,
  CreditText,
  ThumbnailWrapper,
  ThumbnailContainer,
  ThumbnailImage,
  LinkIcon,
} from "./styles";
import { InstructionsBlock } from "../../styles";
import type { Component, ViewerDeviceRuntime } from "../../../types";
import { readPluginConfigString } from "../../../util/readPluginConfigString";
import { IframeFpoThumbnail } from "./IframeFpoThumbnail";
import {
  LEGACY_IFRAME_LINK_OUT_ICON_HTML,
  resolveIframeMobileThumbnailUrl,
  resolveIframeContainerStyle,
  setValidIframeUrl,
} from "./iframeUtils";

/** When the stage element has no author height, avoid `height: 100%` (fills the viewport). */
const DEFAULT_IFRAME_HEIGHT_PX = 400;
const STAGE_ELEMENT_SELECTOR = ".spf-rce-element";

type IframePluginProps = {
  componentConfig: Component & {
    config?: {
      title?: string;
      activityTitle?: string;
      activityLabel?: string;
      url?: string;
      instructions?: string;
      captions?: string;
      credit?: string;
      style?: CSSProperties;
      cssClass?: string;
      thumbnailURL?: unknown;
      thumbnailUrl?: unknown;
    };
  };
  height?: number;
  onLinkClick?: (data: Link) => void;
  renderer?: RendererTypes;
  viewerDevice?: ViewerDeviceRuntime;
};

export function IframePluginComponent({
  componentConfig,
  height,
  onLinkClick,
  renderer,
  viewerDevice,
}: IframePluginProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [thumbnailLoadFailed, setThumbnailLoadFailed] = useState(false);
  const isMobileRenderer = renderer === RendererTypes.MOBILE;

  const raw = (componentConfig?.config ?? {}) as Record<string, unknown>;
  const title = readPluginConfigString(raw, "title");
  const activityTitle = readPluginConfigString(raw, "activityTitle");
  const activityLabel = readPluginConfigString(raw, "activityLabel");
  const rawUrl = readPluginConfigString(raw, "url");
  const instructions = readPluginConfigString(raw, "instructions");
  const captions = readPluginConfigString(raw, "captions");
  const credit = readPluginConfigString(raw, "credit");
  const style = raw.style as CSSProperties | undefined;
  const cssClass = readPluginConfigString(raw, "cssClass");
  const containerStyle = resolveIframeContainerStyle(style, isMobileRenderer);

  const resolvedUrl = rawUrl && rawUrl.length > 1 ? setValidIframeUrl(rawUrl) : undefined;
  const configuredThumbnailUrl = resolveIframeMobileThumbnailUrl(
    raw.thumbnailURL ?? raw.thumbnailUrl,
    viewerDevice,
  );
  const mobileThumbnailUrl =
    configuredThumbnailUrl && !thumbnailLoadFailed ? configuredThumbnailUrl : undefined;

  const resolvedHeightPx =
    typeof height === "number" && !Number.isNaN(height) ? height : DEFAULT_IFRAME_HEIGHT_PX;

  const componentId = componentConfig.$id || "";
  const iframeTitle = activityTitle ?? title ?? "Embedded content";

  useEffect(() => {
    setThumbnailLoadFailed(false);
  }, [configuredThumbnailUrl]);

  const handleThumbnailClick = useCallback(() => {
    if (!resolvedUrl || !onLinkClick) {
      return;
    }
    onLinkClick({
      type: "ExternalLink",
      href: resolvedUrl,
    });
  }, [resolvedUrl, onLinkClick]);

  const handleThumbnailKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleThumbnailClick();
      }
    },
    [handleThumbnailClick],
  );

  useEffect(() => {
    if (!isMobileRenderer) {
      return;
    }

    const container = containerRef.current;
    if (!container) {
      return;
    }

    container.style.height = "auto";

    const resizeObserver = new ResizeObserver(() => {
      const stageEl = container.closest(STAGE_ELEMENT_SELECTOR);
      if (stageEl instanceof HTMLElement) {
        stageEl.style.height = `${container.scrollHeight}px`;
      }
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [
    isMobileRenderer,
    activityLabel,
    activityTitle,
    instructions,
    captions,
    credit,
    resolvedUrl,
    mobileThumbnailUrl,
    thumbnailLoadFailed,
  ]);

  const renderContent = (): JSX.Element | null => {
    if (!resolvedUrl) {
      return null;
    }

    if (isMobileRenderer) {
      return (
        <ThumbnailWrapper className="thumbnail-wrapper">
          <ThumbnailContainer
            className="thumbnail-container"
            role="button"
            tabIndex={0}
            onClick={handleThumbnailClick}
            onKeyDown={handleThumbnailKeyDown}
            aria-label={iframeTitle}
          >
            {mobileThumbnailUrl ? (
              <ThumbnailImage
                className="thumbnail-image"
                src={mobileThumbnailUrl}
                alt=""
                onError={() => setThumbnailLoadFailed(true)}
              />
            ) : (
              <IframeFpoThumbnail />
            )}
            <LinkIcon
              className="link-icon"
              aria-hidden
              dangerouslySetInnerHTML={{ __html: LEGACY_IFRAME_LINK_OUT_ICON_HTML }}
            />
          </ThumbnailContainer>
        </ThumbnailWrapper>
      );
    }

    return <StyledIframe src={resolvedUrl} title={iframeTitle} allowFullScreen />;
  };

  return (
    <Root className="iframe-plugin-root">
      <Container
        ref={containerRef}
        $heightPx={resolvedHeightPx}
        $isMobile={isMobileRenderer}
        className={`iframe-container plugin-iframe readonly ${cssClass ?? ""}`}
        style={containerStyle}
        data-id={componentId}
      >
        <>
          {(activityLabel || activityTitle) && (
            <IframeLabelSection className="component-label-section">
              {activityLabel && (
                <span
                  className="component-label"
                  data-id={`${componentId}-activityLabel`}
                  dangerouslySetInnerHTML={{ __html: activityLabel }}
                />
              )}
              {activityTitle && (
                <span
                  className="component-title"
                  data-id={`${componentId}-activityTitle`}
                  dangerouslySetInnerHTML={{ __html: activityTitle ?? "" }}
                />
              )}
            </IframeLabelSection>
          )}

          {instructions && (
            <InstructionsBlock
              className="component-instructions"
              data-id={`${componentId}-instructions`}
              dangerouslySetInnerHTML={{ __html: instructions }}
            />
          )}
        </>

        {renderContent()}

        {(captions || credit) && (
          <FigureBlock className="component-figure">
            <FigCaption>
              <>
                {captions && (
                  <CaptionText
                    className="component-caption"
                    data-id={`${componentId}-captions`}
                    dangerouslySetInnerHTML={{ __html: captions }}
                  />
                )}
                {credit && (
                  <CreditText
                    className="component-credit"
                    data-id={`${componentId}-credit`}
                    dangerouslySetInnerHTML={{ __html: credit }}
                  />
                )}
              </>
            </FigCaption>
          </FigureBlock>
        )}
      </Container>
    </Root>
  );
}
